import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  ItemCard,
  ItemCardCategory,
  ItemCardSet,
  ItemCardType,
  MemberItemInstance,
  MemberProfileCardUnlock,
} from '../../types/pointItemNote';
import { findItemCard, resolveRemainingUseCount } from '../../utils/memberItemStats';
import type {
  AdminProfileCardSetWithSlots,
  ProfileCardRarity,
  ProfileCardSetSource,
} from '../../types/profileCard';
import { profileCardSlotDisplayName } from '../../types/profileCard';
import CharacterEmptyState from '../common/CharacterEmptyState';
import ProfileCardCachedImage from '../common/ProfileCardCachedImage';
import { profileCardExchangeCoinsForRarity, PROFILE_CARD_RARITY_LABEL } from '../../utils/profileCardRarity';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';

type TypeFilter = 'all' | ItemCardType | 'profile';
type CategoryFilter = 'all' | ItemCardCategory;

export type MemberItemInventoryMember = {
  id: string;
  orderIndex: number;
  name: string;
};

export type MemberInventoryProfileCardSet = AdminProfileCardSetWithSlots & {
  source: ProfileCardSetSource;
  priceCoins: number;
};

type Props = {
  open: boolean;
  member: MemberItemInventoryMember | null;
  instances: MemberItemInstance[];
  itemCards: ItemCardSet;
  /** 학급 포인트 보상 카드 등 ItemCardSet 밖 카탈로그 */
  extraItemCards?: ItemCard[];
  getLevelColor?: (level: number) => string;
  onClose: () => void;
  /** 액티브 카드 사용 — 부모에서 remainingUseCount·usageHistory 반영 후 DB 저장 */
  onUseItem?: (instanceId: string, memo: string) => Promise<void> | void;
  /** 보유 카드 삭제 — 부모에서 인스턴스 제거 후 DB 저장 */
  onDeleteItem?: (instanceId: string) => Promise<void> | void;
  /** 이 멤버가 포함된 전체 프로필 카드 보유 이력(수량 포함) */
  profileCardUnlocks?: MemberProfileCardUnlock[];
  /** 프로필 카드 세트 카탈로그(이름·이미지·등급·가격 조회용) */
  profileCardSets?: MemberInventoryProfileCardSet[];
  /** 중복 카드 코인 환전 */
  onExchangeProfileCardDuplicates?: (params: {
    memberId: string;
    setId: string;
    sourceKind: ProfileCardSetSource;
    slotIndex: number;
  }) => Promise<void> | void;
  /** 세트가 삭제·변경되어 카탈로그에서 찾을 수 없는("알 수 없는 세트") 프로필 카드 보유 이력 삭제 */
  onDeleteProfileCardUnlock?: (params: {
    memberId: string;
    setId: string;
    sourceKind: ProfileCardSetSource;
    slotIndex: number;
  }) => Promise<void> | void;
};

type DisplayRow = {
  inst: MemberItemInstance;
  card: ItemCard | undefined;
  type: ItemCardType;
  exhausted: boolean;
};

type ProfileCardDisplayRow = {
  unlock: MemberProfileCardUnlock;
  setName: string;
  cardName: string;
  source: ProfileCardSetSource;
  storagePath: string | null;
  imageVersion: number;
  quantity: number;
  rarity: ProfileCardRarity | null;
  rarityLabel: string;
  canExchange: boolean;
  coinsOnExchange: number;
  /** 교환 시 차감되는 중복 카드 수(보유 수량 − 1, 1장 유지) */
  deductCount: number;
  /** 세트가 삭제·변경되어 카탈로그에서 찾을 수 없는 카드 */
  isUnknownSet: boolean;
};

const CardStackIcon: React.FC<{ exhausted?: boolean; size?: number }> = ({
  exhausted,
  size = 24,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="2"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill={exhausted ? '#e0e0e0' : '#f0f4f3'}
      stroke={exhausted ? '#999' : '#555'}
      strokeWidth="1.5"
      transform="rotate(-10 8 14)"
    />
    <rect
      x="6"
      y="4"
      width="12"
      height="16"
      rx="2"
      fill={exhausted ? '#e8e8e8' : '#f8f8f8'}
      stroke={exhausted ? '#999' : '#555'}
      strokeWidth="1.5"
    />
    <rect
      x="10"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill={exhausted ? '#eee' : '#fafafa'}
      stroke={exhausted ? '#999' : '#555'}
      strokeWidth="1.5"
      transform="rotate(10 16 14)"
    />
  </svg>
);

function defaultUseMemo(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${month}.${day} 사용`;
}

function formatUsedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${hh}:${mm}`;
}

const MemberItemInventoryModal: React.FC<Props> = ({
  open,
  member,
  instances,
  itemCards,
  extraItemCards,
  getLevelColor,
  onClose,
  onUseItem,
  onDeleteItem,
  profileCardUnlocks = [],
  profileCardSets = [],
  onExchangeProfileCardDuplicates,
  onDeleteProfileCardUnlock,
}) => {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [useTargetId, setUseTargetId] = useState<string | null>(null);
  const [useMemoText, setUseMemoText] = useState('');
  const [using, setUsing] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exchangingKey, setExchangingKey] = useState<string | null>(null);
  const [deleteProfileTargetKey, setDeleteProfileTargetKey] = useState<string | null>(null);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [useToast, setUseToast] = useState<{
    show: boolean;
    itemName: string;
    remainingCount: number;
  }>({ show: false, itemName: '', remainingCount: 0 });

  useEffect(() => {
    if (!open) return;
    setTypeFilter('all');
    setCategoryFilter('all');
    setUseTargetId(null);
    setUseMemoText('');
    setUsing(false);
    setDeleteTargetId(null);
    setDeleting(false);
    setExchangingKey(null);
    setDeleteProfileTargetKey(null);
    setDeletingProfile(false);
    setUseToast({ show: false, itemName: '', remainingCount: 0 });
  }, [open, member?.id]);

  const memberInstances = useMemo(
    () => (member ? instances.filter((i) => i.memberId === member.id) : []),
    [instances, member]
  );

  const rows: DisplayRow[] = useMemo(() => {
    return memberInstances.map((inst) => {
      const card = findItemCard(itemCards, inst.itemCardId, extraItemCards);
      const type: ItemCardType = card?.type ?? 'passive';
      const exhausted =
        type === 'active' &&
        !inst.isLocked &&
        resolveRemainingUseCount(inst, card) <= 0;
      return { inst, card, type, exhausted };
    });
  }, [memberInstances, itemCards, extraItemCards]);

  const unlocked = rows.filter((r) => r.inst.isLocked !== true);

  const ownedCount = unlocked.filter((r) => {
    if (r.type !== 'active') return true;
    return resolveRemainingUseCount(r.inst, r.card) > 0;
  }).length;

  const exhaustedCount = unlocked.filter((r) => r.exhausted).length;

  const typeCounts = {
    all: rows.length,
    passive: rows.filter((r) => r.type === 'passive').length,
    active: rows.filter((r) => r.type === 'active').length,
  };

  const filtered = useMemo(() => {
    return [...rows]
      .sort((a, b) => {
        const lockDiff = Number(!!a.inst.isLocked) - Number(!!b.inst.isLocked);
        if (lockDiff !== 0) return lockDiff;
        return Number(a.exhausted) - Number(b.exhausted);
      })
      .filter((r) => {
        if (typeFilter !== 'all' && r.type !== typeFilter) return false;
        if (categoryFilter !== 'all' && r.inst.category !== categoryFilter) return false;
        return true;
      });
  }, [rows, typeFilter, categoryFilter]);

  const profileCardRows: ProfileCardDisplayRow[] = useMemo(() => {
    if (!member) return [];
    return profileCardUnlocks
      .filter((u) => u.memberId === member.id && (u.quantity ?? 1) > 0)
      .map((u) => {
        const sourceKind = u.sourceKind ?? 'admin';
        const set = profileCardSets.find((s) => s.id === u.setId && s.source === sourceKind);
        const slot = set?.slots.find((s) => s.slotIndex === u.slotIndex);
        const quantity = u.quantity ?? 1;
        const rarity = slot?.rarity ?? null;
        const canExchange = rarity != null && quantity >= 2;
        const deductCount = canExchange ? quantity - 1 : 0;
        const coinsPerCard = canExchange
          ? profileCardExchangeCoinsForRarity(set?.priceCoins ?? 0, rarity)
          : 0;
        const coinsOnExchange = coinsPerCard * deductCount;
        return {
          unlock: u,
          setName: set?.name ?? '(알 수 없는 세트)',
          cardName: profileCardSlotDisplayName(slot, u.slotIndex),
          source: sourceKind,
          storagePath: slot?.storagePath ?? null,
          imageVersion: slot?.imageVersion ?? 0,
          quantity,
          rarity,
          rarityLabel: rarity ? PROFILE_CARD_RARITY_LABEL[rarity] : '',
          canExchange,
          coinsOnExchange,
          deductCount,
          isUnknownSet: !set,
        };
      })
      .sort((a, b) => a.setName.localeCompare(b.setName) || a.unlock.slotIndex - b.unlock.slotIndex);
  }, [member, profileCardUnlocks, profileCardSets]);

  const useTarget = useMemo(() => {
    if (!useTargetId) return null;
    return rows.find((r) => r.inst.id === useTargetId) ?? null;
  }, [useTargetId, rows]);

  const deleteTarget = useMemo(() => {
    if (!deleteTargetId) return null;
    return rows.find((r) => r.inst.id === deleteTargetId) ?? null;
  }, [deleteTargetId, rows]);

  const deleteProfileTarget = useMemo(() => {
    if (!deleteProfileTargetKey) return null;
    return (
      profileCardRows.find(
        (r) => `${r.source}:${r.unlock.setId}:${r.unlock.slotIndex}` === deleteProfileTargetKey
      ) ?? null
    );
  }, [deleteProfileTargetKey, profileCardRows]);

  if (!open || !member) return null;

  const title = `${member.orderIndex} ${member.name?.trim() || '(이름 없음)'}`;
  const emptyMessage = (() => {
    if (typeFilter === 'passive') return '보유한 패시브 카드가 없어요!';
    if (typeFilter === 'active') return '보유한 액티브 카드가 없어요!';
    if (categoryFilter === 'levelup') return '보유한 레벨업 카드가 없어요!';
    if (categoryFilter === 'assignment') return '보유한 과제보상 카드가 없어요!';
    if (categoryFilter === 'store') return '보유한 스토어 카드가 없어요!';
    if (categoryFilter === 'classPoint') return '보유한 학급 포인트 보상 카드가 없어요!';
    return '보유한 아이템 카드가 없어요!';
  })();

  const handleExchange = async (row: ProfileCardDisplayRow) => {
    if (!onExchangeProfileCardDuplicates || !row.canExchange) return;
    const key = `${row.source}:${row.unlock.setId}:${row.unlock.slotIndex}`;
    setExchangingKey(key);
    try {
      await onExchangeProfileCardDuplicates({
        memberId: member.id,
        setId: row.unlock.setId,
        sourceKind: row.source,
        slotIndex: row.unlock.slotIndex,
      });
    } finally {
      setExchangingKey(null);
    }
  };

  const openUseModal = (row: DisplayRow) => {
    if (row.inst.isLocked || row.type !== 'active') return;
    setUseTargetId(row.inst.id);
    setUseMemoText(defaultUseMemo());
  };

  const closeUseModal = () => {
    if (using) return;
    setUseTargetId(null);
    setUseMemoText('');
  };

  const confirmUse = async () => {
    if (!useTarget || !onUseItem) return;
    const remaining = resolveRemainingUseCount(useTarget.inst, useTarget.card);
    if (remaining <= 0) return;
    setUsing(true);
    try {
      await onUseItem(useTarget.inst.id, useMemoText.trim());
      const name = useTarget.card?.name?.trim() || '(이름 없음)';
      setUseToast({
        show: true,
        itemName: name,
        remainingCount: Math.max(0, remaining - 1),
      });
      setUseTargetId(null);
      setUseMemoText('');
      window.setTimeout(() => {
        setUseToast({ show: false, itemName: '', remainingCount: 0 });
      }, 2500);
    } finally {
      setUsing(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !onDeleteItem) return;
    setDeleting(true);
    try {
      await onDeleteItem(deleteTarget.inst.id);
      setDeleteTargetId(null);
      if (useTargetId === deleteTarget.inst.id) {
        setUseTargetId(null);
        setUseMemoText('');
      }
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteProfile = async () => {
    if (!deleteProfileTarget || !onDeleteProfileCardUnlock || !member) return;
    setDeletingProfile(true);
    try {
      await onDeleteProfileCardUnlock({
        memberId: member.id,
        setId: deleteProfileTarget.unlock.setId,
        sourceKind: deleteProfileTarget.source,
        slotIndex: deleteProfileTarget.unlock.slotIndex,
      });
      setDeleteProfileTargetKey(null);
    } finally {
      setDeletingProfile(false);
    }
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-apn-modal cp-pin-item-inv-modal"
        data-tutorial-pin="inventory-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-item-inv-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header">
          <h2 id="cp-pin-item-inv-title">{title}</h2>
          <button type="button" className="cp-apn-icon-btn" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-item-inv-body">
          {typeFilter === 'profile' ? (
            <div className="cp-pin-item-inv-summary">
              <div className="cp-pin-item-inv-summary-item">
                <span className="cp-pin-item-inv-summary-label">보유 프로필 카드 종류</span>
                <span className="cp-pin-item-inv-summary-value">
                  <CardStackIcon />
                  {profileCardRows.length}개
                </span>
              </div>
            </div>
          ) : (
            <div className="cp-pin-item-inv-summary">
              <div className="cp-pin-item-inv-summary-item">
                <span className="cp-pin-item-inv-summary-label">보유 아이템 카드</span>
                <span className="cp-pin-item-inv-summary-value">
                  <CardStackIcon />
                  {ownedCount}개
                </span>
              </div>
              <div className="cp-pin-item-inv-summary-item">
                <span className="cp-pin-item-inv-summary-label">사용완료 카드</span>
                <span className="cp-pin-item-inv-summary-value is-exhausted">
                  <CardStackIcon exhausted />
                  {exhaustedCount}개
                </span>
              </div>
            </div>
          )}

          <div className="cp-pin-item-inv-type-filter" role="tablist" aria-label="카드 유형">
            {(
              [
                { key: 'all', label: '전체 카드', short: '전체', count: typeCounts.all },
                { key: 'passive', label: '패시브 카드', short: '패시브', count: typeCounts.passive },
                { key: 'active', label: '액티브 카드', short: '액티브', count: typeCounts.active },
                { key: 'profile', label: '프로필 카드', short: '프로필', count: profileCardRows.length },
              ] as const
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                className={typeFilter === t.key ? 'is-active' : ''}
                role="tab"
                aria-selected={typeFilter === t.key}
                onClick={() => setTypeFilter(t.key)}
              >
                <span className="cp-pin-item-inv-type-left">
                  <span className="full-text">{t.label}</span>
                  <span className="short-text">{t.short}</span>
                </span>
                <span className="cp-pin-item-inv-type-count">{t.count}개</span>
              </button>
            ))}
          </div>

          {typeFilter !== 'profile' ? (
            <div className="cp-pin-item-inv-cat-filter" role="group" aria-label="카테고리">
              {(
                [
                  { key: 'all', full: '전체 카테고리', short: '전체' },
                  { key: 'levelup', full: '레벨업 카드', short: '레벨업' },
                  { key: 'assignment', full: '과제보상 카드', short: '과제보상' },
                  { key: 'store', full: '스토어 카드', short: '스토어' },
                  { key: 'classPoint', full: '학급 포인트 카드', short: '학급' },
                ] as const
              ).map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={categoryFilter === c.key ? 'is-active' : ''}
                  onClick={() => setCategoryFilter(c.key)}
                >
                  <span className="full-text">{c.full}</span>
                  <span className="short-text">{c.short}</span>
                </button>
              ))}
            </div>
          ) : null}

          {typeFilter === 'profile' ? (
            <div className="cp-pin-item-inv-grid cp-pin-item-inv-grid--profile">
              {profileCardRows.length === 0 ? (
                <CharacterEmptyState
                  variant="modal"
                  className="cp-pin-item-inv-empty"
                  message="보유한 프로필 카드가 없어요!"
                />
              ) : (
                profileCardRows.map((row) => {
                  const key = `${row.source}:${row.unlock.setId}:${row.unlock.slotIndex}`;
                  const isExchanging = exchangingKey === key;
                  return (
                    <article key={row.unlock.id} className="cp-pin-item-inv-card cp-pin-item-inv-profile-card">
                      <div className="cp-pin-item-inv-card-icon cp-pin-item-inv-profile-card-icon">
                        <div className="cp-pin-item-inv-card-icon-media">
                          {row.storagePath ? (
                            <ProfileCardCachedImage
                              source={row.source}
                              storagePath={row.storagePath}
                              imageVersion={row.imageVersion}
                              alt=""
                            />
                          ) : (
                            <CardStackIcon />
                          )}
                        </div>
                        {row.isUnknownSet ? (
                          <button
                            type="button"
                            className="cp-pin-item-inv-del-btn cp-pin-item-inv-del-btn--always"
                            title="카드 삭제"
                            aria-label="카드 삭제"
                            disabled={!onDeleteProfileCardUnlock}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!onDeleteProfileCardUnlock) return;
                              setDeleteProfileTargetKey(key);
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 11v6M14 11v6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        ) : null}
                      </div>
                      <p className="cp-pin-item-inv-card-name">{row.setName}</p>
                      <p className="cp-pin-item-inv-card-memo">
                        {row.rarityLabel
                          ? `${row.rarityLabel} · ${row.cardName}`
                          : row.cardName}
                      </p>
                      {row.quantity > 1 ? (
                        <span className="cp-pin-item-inv-profile-qty-tag">x{row.quantity}</span>
                      ) : null}
                      {row.canExchange ? (
                        <button
                          type="button"
                          className="cp-pin-item-inv-profile-exchange-btn"
                          disabled={isExchanging || !onExchangeProfileCardDuplicates}
                          onClick={() => void handleExchange(row)}
                        >
                          {isExchanging ? (
                            '교환 중…'
                          ) : (
                            <>
                              <span className="cp-pin-item-inv-profile-exchange-btn-title">
                                중복 카드 코인 교환
                              </span>
                              <span className="cp-pin-item-inv-profile-exchange-btn-desc">
                                {`+${row.coinsOnExchange}코인, 카드 ${row.deductCount}개 차감`}
                              </span>
                            </>
                          )}
                        </button>
                      ) : null}
                    </article>
                  );
                })
              )}
            </div>
          ) : (
          <div className="cp-pin-item-inv-grid">
            {filtered.length === 0 ? (
              <CharacterEmptyState
                variant="modal"
                className="cp-pin-item-inv-empty"
                message={emptyMessage}
              />
            ) : (
              filtered.map((row) => {
                const { inst, card, type, exhausted } = row;
                const cat = inst.category;
                const badge =
                  cat === 'store'
                    ? 'S'
                    : cat === 'assignment'
                      ? 'A'
                      : cat === 'levelup'
                        ? 'L'
                        : cat === 'classPoint'
                          ? 'C'
                          : '';
                const levelColor =
                  typeof inst.acquiredLevel === 'number' && getLevelColor
                    ? getLevelColor(inst.acquiredLevel)
                    : undefined;
                const clickable = type === 'active' && !inst.isLocked;
                return (
                  <article
                    key={inst.id}
                    className={`cp-pin-item-inv-card${exhausted ? ' is-exhausted' : ''}${
                      type === 'active' && !exhausted && !inst.isLocked ? ' is-active-card' : ''
                    }${inst.isLocked ? ' is-locked' : ''}${clickable ? ' is-clickable' : ''}`}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={() => {
                      if (clickable) openUseModal(row);
                    }}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openUseModal(row);
                      }
                    }}
                  >
                    <div className="cp-pin-item-inv-card-icon">
                      <div className="cp-pin-item-inv-card-icon-media">
                        {resolveItemCardImageSrc(card?.cardImage) ? (
                          <img src={resolveItemCardImageSrc(card?.cardImage)!} alt="" />
                        ) : (
                          <CardStackIcon exhausted={exhausted} />
                        )}
                      </div>
                      {cat === 'levelup' && typeof inst.acquiredLevel === 'number' ? (
                        <span
                          className="cp-pin-item-inv-level-badge"
                          style={levelColor ? { background: levelColor } : undefined}
                        >
                          레벨{inst.acquiredLevel}
                        </span>
                      ) : null}
                      <span
                        className={`cp-pin-item-inv-cat-badge is-${cat}`}
                        title={
                          cat === 'store'
                            ? '스토어'
                            : cat === 'assignment'
                              ? '과제보상'
                              : cat === 'classPoint'
                                ? '학급 포인트'
                                : '레벨업'
                        }
                      >
                        {badge}
                      </span>
                      <button
                        type="button"
                        className="cp-pin-item-inv-del-btn"
                        title="카드 삭제"
                        aria-label="카드 삭제"
                        disabled={inst.isLocked === true || !onDeleteItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (inst.isLocked || !onDeleteItem) return;
                          setDeleteTargetId(inst.id);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 11v6M14 11v6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="cp-pin-item-inv-card-name">
                      {card?.name?.trim() || '(이름 없음)'}
                    </p>
                    <p className="cp-pin-item-inv-card-memo">
                      {card?.memo?.trim() || '아이템 카드 설명이 없습니다.'}
                    </p>
                    {inst.isLocked ? (
                      <div className="cp-pin-item-inv-card-foot is-locked">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="11"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <path
                            d="M7 11V7a5 5 0 0 1 10 0v4"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                        잠금
                      </div>
                    ) : type === 'active' ? (
                      <div className="cp-pin-item-inv-card-foot">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                            fill="#e74c3c"
                          />
                        </svg>
                        × {resolveRemainingUseCount(inst, card)}
                      </div>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
          )}
        </div>

        {useTarget ? (
          <div
            className="cp-pin-item-use-overlay"
            role="presentation"
            onClick={closeUseModal}
          >
            <div
              className="cp-pin-item-use-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-pin-item-use-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="cp-pin-item-use-header">
                <h3 id="cp-pin-item-use-title">
                  {useTarget.card?.name?.trim() || '(이름 없음)'}
                </h3>
                <button
                  type="button"
                  className="cp-apn-icon-btn"
                  onClick={closeUseModal}
                  aria-label="닫기"
                >
                  ×
                </button>
              </header>
              <div className="cp-pin-item-use-body">
                <p className="cp-pin-item-use-message">
                  {useTarget.card?.name?.trim() || '아이템'} 카드 사용!
                </p>
                {(useTarget.inst.usageHistory?.length ?? 0) > 0 ? (
                  <div className="cp-pin-item-use-history">
                    <p className="cp-pin-item-use-history-title">사용 내역</p>
                    <ul className="cp-pin-item-use-history-list">
                      {[...(useTarget.inst.usageHistory ?? [])]
                        .slice()
                        .reverse()
                        .map((h, idx) => (
                          <li key={`${h.usedAt}-${idx}`}>
                            <span className="cp-pin-item-use-history-date">
                              {formatUsedAt(h.usedAt)}
                            </span>
                            {h.memo ? (
                              <span className="cp-pin-item-use-history-memo">{h.memo}</span>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : null}
                {resolveRemainingUseCount(useTarget.inst, useTarget.card) > 0 ? (
                  <div className="cp-pin-item-use-input">
                    <input
                      type="text"
                      value={useMemoText}
                      maxLength={20}
                      placeholder="메모 입력 (선택, 최대 20자)"
                      onChange={(e) => setUseMemoText(e.target.value.slice(0, 20))}
                      onFocus={(e) => {
                        if (e.target.value === defaultUseMemo()) setUseMemoText('');
                      }}
                    />
                    <span className="cp-pin-item-use-counter">{useMemoText.length}/20</span>
                  </div>
                ) : (
                  <p className="cp-pin-item-use-exhausted">모든 사용 횟수를 소진한 카드입니다.</p>
                )}
              </div>
              <footer className="cp-pin-item-use-footer">
                <button type="button" className="cp-apn-btn" onClick={closeUseModal} disabled={using}>
                  {resolveRemainingUseCount(useTarget.inst, useTarget.card) > 0 ? '취소' : '닫기'}
                </button>
                {resolveRemainingUseCount(useTarget.inst, useTarget.card) > 0 ? (
                  <button
                    type="button"
                    className="cp-apn-btn cp-apn-btn-primary"
                    onClick={() => void confirmUse()}
                    disabled={using || !onUseItem}
                  >
                    {using ? '사용 중…' : '사용'}
                  </button>
                ) : null}
              </footer>
            </div>
          </div>
        ) : null}

        {deleteTarget ? (
          <div
            className="cp-pin-item-use-overlay"
            role="presentation"
            onClick={() => {
              if (!deleting) setDeleteTargetId(null);
            }}
          >
            <div
              className="cp-pin-item-use-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-pin-item-del-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="cp-pin-item-use-header">
                <h3 id="cp-pin-item-del-title">카드 삭제</h3>
                <button
                  type="button"
                  className="cp-apn-icon-btn"
                  onClick={() => setDeleteTargetId(null)}
                  aria-label="닫기"
                  disabled={deleting}
                >
                  ×
                </button>
              </header>
              <div className="cp-pin-item-use-body">
                <p className="cp-pin-item-use-message">
                  {deleteTarget.card?.name?.trim() || '(이름 없음)'}
                </p>
                <p className="cp-pin-item-del-desc">카드를 삭제하시겠습니까?</p>
                <p className="cp-pin-item-del-warn">이 작업은 되돌릴 수 없습니다.</p>
              </div>
              <footer className="cp-pin-item-use-footer">
                <button
                  type="button"
                  className="cp-apn-btn"
                  onClick={() => setDeleteTargetId(null)}
                  disabled={deleting}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="cp-apn-btn cp-apn-btn-danger"
                  onClick={() => void confirmDelete()}
                  disabled={deleting || !onDeleteItem}
                >
                  {deleting ? '삭제 중…' : '삭제'}
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {deleteProfileTarget ? (
          <div
            className="cp-pin-confirm-overlay"
            role="presentation"
            onClick={() => {
              if (!deletingProfile) setDeleteProfileTargetKey(null);
            }}
          >
            <div
              className="cp-pin-confirm cp-pin-confirm--warn"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-pin-profile-del-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cp-pin-confirm-warn-icon" aria-hidden>
                !
              </div>
              <p id="cp-pin-profile-del-title" className="cp-pin-confirm-warn-title">
                카드 삭제
              </p>
              <p className="cp-pin-confirm-warn-desc">
                「{deleteProfileTarget.setName} · {deleteProfileTarget.cardName}」는 세트 정보가
                변경되어 더 이상 확인할 수 없는 카드예요. 삭제할까요?
              </p>
              <div className="cp-pin-confirm-actions cp-pin-confirm-actions--cancel-wide">
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                  onClick={() => setDeleteProfileTargetKey(null)}
                  disabled={deletingProfile}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                  onClick={() => void confirmDeleteProfile()}
                  disabled={deletingProfile || !onDeleteProfileCardUnlock}
                >
                  {deletingProfile ? '삭제 중…' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {useToast.show ? (
          <div className="cp-pin-item-use-toast" role="status">
            <CardStackIcon size={40} />
            <div>
              <strong>{useToast.itemName}</strong>
              <span>사용 완료 · 남은 횟수 {useToast.remainingCount}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default MemberItemInventoryModal;
